/**
 * MapView (Iteration 12 — N7)
 * 
 * Wraps react-map-gl (Mapbox GL JS) — single map component for all manual count surfaces.
 * Marker colors bound to theme tokens via statusTone.js — NO hex literals.
 * Viewport change callback debounced (300ms).
 * 
 * Props:
 *   markers: [{id, lat, lng, status, label, scopeName, supplyManual, demandManual}]
 *   onViewportChange: (bounds) => void
 *   onMarkerClick: (marker) => void
 *   initialCenter: {lat, lng}
 *   initialZoom: number
 *   children: React.ReactNode (for overlays like scoreboard toggle)
 */

import React, { useCallback, useRef, useState } from 'react';
import { Map, Marker, NavigationControl } from 'react-map-gl';
import { Box, Skeleton, Typography } from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import 'mapbox-gl/dist/mapbox-gl.css';

import LocationMarker from './LocationMarker';
import MarkerPopup from './MarkerPopup';

const MAPBOX_TOKEN = import.meta.env.VITE_MAPBOX_TOKEN || '';
const DEBOUNCE_MS = 300;

export default function MapView({
  markers = [],
  onViewportChange,
  onMarkerClick,
  initialCenter = { lat: 46.8, lng: -92.1 }, // Duluth, MN default
  initialZoom = 12,
  children,
  loading = false,
  error = null,
  emptyMessage = 'No locations defined yet — create a project to get started',
  sx = {},
}) {
  const theme = useTheme();
  const debounceRef = useRef(null);
  const [selectedMarker, setSelectedMarker] = useState(null);

  const handleMoveEnd = useCallback((evt) => {
    if (!onViewportChange) return;

    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      const bounds = evt.target.getBounds();
      onViewportChange({
        north: bounds.getNorth(),
        south: bounds.getSouth(),
        east: bounds.getEast(),
        west: bounds.getWest(),
      });
    }, DEBOUNCE_MS);
  }, [onViewportChange]);

  const handleMarkerClick = useCallback((marker) => {
    setSelectedMarker(marker);
    if (onMarkerClick) onMarkerClick(marker);
  }, [onMarkerClick]);

  if (!MAPBOX_TOKEN) {
    return (
      <Box sx={{ p: 4, textAlign: 'center', ...sx }}>
        <Typography color="error">
          Map unavailable — set VITE_MAPBOX_TOKEN in environment variables.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ position: 'relative', width: '100%', height: '100%', minHeight: 400, ...sx }}>
      {/* Loading overlay */}
      {loading && (
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 10,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: alpha(theme.palette.common.black, 0.3),
          }}
        >
          <Skeleton variant="rectangular" width="100%" height="100%" />
        </Box>
      )}

      {/* Error overlay */}
      {error && (
        <Box
          sx={{
            position: 'absolute',
            top: 16,
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 10,
            backgroundColor: theme.palette.error.dark,
            color: theme.palette.error.contrastText,
            px: 3,
            py: 1,
            borderRadius: 1,
          }}
        >
          <Typography variant="body2">{error}</Typography>
        </Box>
      )}

      {/* Empty state overlay */}
      {!loading && !error && markers.length === 0 && (
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            zIndex: 10,
            backgroundColor: theme.palette.background.paper,
            px: 4,
            py: 2,
            borderRadius: 2,
            border: `1px solid ${theme.palette.divider}`,
            textAlign: 'center',
          }}
        >
          <Typography variant="body1" color="text.secondary">
            {emptyMessage}
          </Typography>
        </Box>
      )}

      <Map
        initialViewState={{
          latitude: initialCenter.lat,
          longitude: initialCenter.lng,
          zoom: initialZoom,
        }}
        mapboxAccessToken={MAPBOX_TOKEN}
        mapStyle="mapbox://styles/mapbox/dark-v11"
        onMoveEnd={handleMoveEnd}
        style={{ width: '100%', height: '100%' }}
      >
        <NavigationControl position="top-left" />

        {/* Markers */}
        {markers.map((marker) => (
          <Marker
            key={marker.id}
            latitude={marker.lat}
            longitude={marker.lng}
            anchor="bottom"
            onClick={(e) => {
              e.originalEvent.stopPropagation();
              handleMarkerClick(marker);
            }}
          >
            <LocationMarker
              status={marker.status}
              label={marker.label}
              isSelected={selectedMarker?.id === marker.id}
            />
          </Marker>
        ))}

        {/* Marker popup */}
        {selectedMarker && (
          <MarkerPopup
            marker={selectedMarker}
            onClose={() => setSelectedMarker(null)}
          />
        )}
      </Map>

      {/* Children (scoreboard toggle, etc.) */}
      {children}
    </Box>
  );
}
